import {
    HISTORY_FIELD_LABELS,
    HistoryField,
} from "@common/constants/history-source-types";
import { HistoryDetailsItem, ResourceType } from "@common/types/auditLog";
import {
    ScheduleActivityIdentity,
    scheduleActivityMetadataSchema,
    ScheduleActivitySettlement,
} from "@common/schemas/domains/accounting/scheduleActivity.schema";
import { currencyFormatterFractionsIfNeeded } from "@common/helpers/currency";
import { ScheduleStopSettlementKind } from "@common/types/domains/accounting/schedule";
import { resolveSingleChangedField } from "../../utils/scheduleActivityDescription";
import { SCHEDULES_HUB_TABS } from "@components/accounting/schedules/SchedulesHubTabs";
import { buildScheduleHubUrl } from "@components/accounting/schedulesReport/schedulesReportDrillDown.lib";
import { HistoryDetailsBaseRow } from "../cells/historyDetailsBaseRow";

export enum ScheduleActivityTableKind {
    IDENTITY = "identity",
    CHANGE = "change",
}

export interface ScheduleActivityRow extends HistoryDetailsBaseRow {
    id: string;
    policyName: string | null;
    group: string | null;
    sourceLabel: string | null;
    previous: string | null;
    modified: string | null;
    link: string | null;
}

export interface ScheduleActivityTable {
    kind: ScheduleActivityTableKind;
    changedFieldLabel: string | null;
    rows: ScheduleActivityRow[];
}

interface BuildScheduleActivityTableParams {
    sourceType: string;
    details: HistoryDetailsItem[];
}

interface ParsedDetail {
    detail: HistoryDetailsItem;
    identity: ScheduleActivityIdentity | null;
    changedFields: string[];
    settlement: ScheduleActivitySettlement | null;
}

interface ChangePair {
    previous: string | null;
    modified: string | null;
}

interface TableChange {
    label: string;
    pairFor: (parsed: ParsedDetail) => ChangePair;
}

function parseDetail(detail: HistoryDetailsItem): ParsedDetail {
    const parsed = scheduleActivityMetadataSchema.safeParse(
        detail.metadata?.activity,
    );
    return {
        detail,
        identity: parsed.success ? parsed.data.identity : null,
        changedFields: parsed.success ? parsed.data.changedFields : [],
        settlement: parsed.success ? (parsed.data.settlement ?? null) : null,
    };
}

function buildLink({
    detail,
    identity,
}: Pick<ParsedDetail, "detail" | "identity">): string | null {
    if (!identity) {
        return null;
    }
    if (detail.resourceType === ResourceType.SCHEDULE_TEMPLATE) {
        return buildScheduleHubUrl({
            tab: SCHEDULES_HUB_TABS.policies,
            templateId: identity.templateId,
        });
    }
    return buildScheduleHubUrl({
        templateId: identity.templateId,
        scheduleApplicationId: detail.resourceId,
    });
}

interface FormatDiffValueParams {
    field: HistoryField;
    value: string | number | null | undefined;
}

function formatDiffValue({
    field,
    value,
}: FormatDiffValueParams): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (field === HistoryField.AMOUNT && typeof value === "number") {
        return currencyFormatterFractionsIfNeeded.format(value);
    }
    return String(value);
}

const SETTLEMENT_ACCOUNT_LABEL = "account";

interface ResolveChangedFieldParams {
    sourceType: string;
    parsedDetails: ParsedDetail[];
}

function resolveSharedSingleChangedField({
    sourceType,
    parsedDetails,
}: ResolveChangedFieldParams): HistoryField | null {
    const fields = parsedDetails.map(({ changedFields }) =>
        resolveSingleChangedField({ sourceType, changedFields }),
    );
    const [field] = fields;
    if (!field || fields.some((candidate) => candidate !== field)) {
        return null;
    }
    return field;
}

function resolveTableChange(
    params: ResolveChangedFieldParams,
): TableChange | null {
    const isReclass =
        params.parsedDetails.length > 0 &&
        params.parsedDetails.every(
            ({ settlement }) =>
                settlement?.kind === ScheduleStopSettlementKind.reclass,
        );
    if (isReclass) {
        return {
            label: SETTLEMENT_ACCOUNT_LABEL,
            pairFor: ({ settlement }) => ({
                previous: settlement?.fromAccount ?? null,
                modified: settlement?.toAccount ?? null,
            }),
        };
    }
    const field = resolveSharedSingleChangedField(params);
    if (!field) {
        return null;
    }
    return {
        label: HISTORY_FIELD_LABELS[field],
        pairFor: ({ detail }) => {
            const diff = detail.diff.find((entry) => entry.field === field);
            return {
                previous: formatDiffValue({ field, value: diff?.before.value }),
                modified: formatDiffValue({ field, value: diff?.after.value }),
            };
        },
    };
}

export function buildScheduleActivityTable({
    sourceType,
    details,
}: BuildScheduleActivityTableParams): ScheduleActivityTable {
    const parsedDetails = details.map(parseDetail);
    const change = resolveTableChange({ sourceType, parsedDetails });
    const rows = parsedDetails.map((parsed) => {
        const { detail, identity } = parsed;
        const pair = change?.pairFor(parsed);
        return {
            id: detail.id,
            date: detail.createdAt,
            logDate: detail.createdAt,
            entityId: detail.entityId,
            policyName: identity?.policyName ?? null,
            group: identity?.group ?? null,
            sourceLabel: identity?.sourceLabel ?? null,
            previous: pair?.previous ?? null,
            modified: pair?.modified ?? null,
            link: buildLink({ detail, identity }),
        };
    });
    return {
        kind: change
            ? ScheduleActivityTableKind.CHANGE
            : ScheduleActivityTableKind.IDENTITY,
        changedFieldLabel: change?.label ?? null,
        rows,
    };
}
